import { useEffect, useRef, useState } from 'react';
import { t } from '../../i18n';

/**
 * Recording from the microphone in the browser (MediaRecorder): start,
 * stop, and the seconds so far. The recording goes to onDone as WebM or MP4,
 * whichever the browser makes; the server converts it. The callbacks
 * called are the latest ones given, not those of the render that started
 * recording (which wouldn't know, e.g., which language it was for).
 */
export function useRecorder(onDone: (audio: Blob) => void, onError: (message: string) => void) {
  const [state, setState] = useState<{ stop: () => void; startedAt: number } | null>(null);
  const [now, setNow] = useState(Date.now());
  const callbacks = useRef({ onDone, onError });
  callbacks.current = { onDone, onError };

  useEffect(() => {
    if (!state) return;
    const timer = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(timer);
  }, [state]);

  async function start() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      const media = new MediaRecorder(stream);
      const chunks: Blob[] = [];
      media.ondataavailable = (e) => chunks.push(e.data);
      media.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        setState(null);
        callbacks.current.onDone(new Blob(chunks, { type: media.mimeType }));
      };
      media.start();
      setState({ stop: () => media.stop(), startedAt: Date.now() });
    } catch {
      callbacks.current.onError(t('The microphone isn’t available. Allow it in the browser, or upload a file.'));
    }
  }

  return { recording: state !== null, seconds: state ? Math.floor((now - state.startedAt) / 1000) : 0, start, stop: () => state?.stop() };
}
